import React from "react";
import { Text, View, StyleSheet, ActivityIndicator } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import Logo from "../../general/logos/Logo";
import { useAppConfig } from "../../../AppConfigProvider";

function LoginForm({
  email,
  password,
  isValidEmail,
  errorMessage,
  logo,
  onEmailChangeText,
  onEmailBlur,
  onPasswordChangeText,
  onPasswordToggle,
  passwordHidden,
  onSubmit,
  onResetPasswordPress,
  onSignupPress,
  isLoading,
  hasSAMLSSO,
  onSAMLSSOPress,
  requireSSO,
}) {
  const { primaryColor, allowFormSignup } = useAppConfig();
  return (
    <View style={{ flex: 1, width: "100%" }}>
      {hasSAMLSSO && requireSSO ? (
        <View style={styles.container}>
          <View style={styles.card}>
            <View style={styles.cardContent}>
              <View style={styles.logoWrapper}>
                <Logo uri={logo} height={40} />
              </View>
              <CustomText
                useTranslationText={false}
                text="Sign in"
                textType="display"
                size="xs"
                weight="bold"
                style={{ textAlign: "center", marginBottom: 8, lineHeight: 32 }}
              />
              <CustomText
                useTranslationText={false}
                text="Access your courses and assignments"
                size="s"
                weight="bold"
                style={styles.subtitle}
              />
              <View style={{ width: "100%" }}>
                <CustomButton
                  text="Sign in with your school account"
                  accessibilityHint="Press to sign in with Single Sign-On"
                  styleType="primary"
                  disabled={isLoading}
                  size="m"
                  onPress={onSAMLSSOPress}
                  style={{
                    paddingVertical: 16,
                    paddingHorizontal: 24,
                    borderRadius: 6,
                    width: "100%",
                    marginBottom: 24,
                  }}
                />
              </View>
              <View style={styles.helpContainer}>
                <Text style={styles.helpText}>
                  Having trouble signing in?{" "}
                  <Text
                    style={[styles.helpLink, { color: primaryColor }]}
                    onPress={() => {
                      // open mailto or help page
                    }}>
                    Contact your school administrator
                  </Text>
                </Text>
              </View>
            </View>
          </View>
        </View>
      ) : (
        <View style={styles.container}>
          <View style={styles.cardContent}>
            <View style={styles.card}>
              <View style={styles.header}>
                <Logo uri={logo} />
                <CustomSpacing type="vertical" size="m" />
                <CustomText
                  text="sign_in_to_companion"
                  textType="display"
                  size="xs"
                  weight="bold"
                  style={styles.headerTitle}
                />
              </View>

              <CustomSpacing type="vertical" size="m" />
              <CustomText text="Email Address" size="s" />
              <CustomSpacing type="vertical" size="s" />
              <View style={{ width: "100%" }}>
                <CustomTextInput
                  style={[
                    !isValidEmail && styles.textInputError,
                    { backgroundColor: "#F8F8F8", borderColor: "#DEDEDE" },
                  ]}
                  value={email}
                  placeholder="Email..."
                  onChangeText={(text) => onEmailChangeText(text)}
                  onBlur={onEmailBlur}
                  autoComplete="email"
                  aria-label="Email text input"
                  accessibilityHint="Enter your email address"
                />
              </View>
              <View aria-live={isValidEmail ? "assertive" : "none"}>
                {!isValidEmail ? (
                  <>
                    <CustomSpacing type="vertical" size="xs" />
                    <CustomText text="Please enter a valid email." role="alert" size="xs" style={styles.errorText} />
                  </>
                ) : (
                  <CustomSpacing type="vertical" size="m" />
                )}
              </View>
              <CustomSpacing type="vertical" size="s" />

              <CustomText text="Password" size="s" />
              <CustomSpacing type="vertical" size="s" />
              <View style={{ width: "100%" }}>
                <CustomTextInput
                  value={password}
                  style={{ backgroundColor: "#F8F8F8", borderColor: "#DEDEDE" }}
                  placeholder="Password..."
                  onChangeText={(text) => onPasswordChangeText(text)}
                  secureTextEntry={passwordHidden}
                  onToggleSecureEntry={onPasswordToggle}
                  aria-label="Password text input"
                  accessibilityHint="Enter your password"
                />
              </View>

              <View aria-live={errorMessage ? "assertive" : "none"}>
                {errorMessage ? (
                  <>
                    <CustomSpacing type="vertical" size="s" />
                    <CustomText role="alert" text={errorMessage} size="s" style={styles.errorMessage} />
                    <CustomSpacing type="vertical" size="s" />
                  </>
                ) : (
                  <CustomSpacing type="vertical" size="xl" />
                )}
              </View>

              {isLoading && !errorMessage ? (
                <View style={styles.loader}>
                  <ActivityIndicator color={primaryColor} />
                </View>
              ) : (
                <CustomButton
                  text="Sign in"
                  accessibilityHint="Press to sign in"
                  size="m"
                  bold={true}
                  styleType="primary"
                  style={styles.primaryFullButton}
                  onPress={onSubmit}
                  disabled={isLoading}
                />
              )}
              {hasSAMLSSO && (
                <>
                  <CustomSpacing type="vertical" size="l" />
                  <CustomButton
                    text="Sign in with SSO"
                    accessibilityHint="Press to sign in with Single Sign-On"
                    size="m"
                    styleType="secondary"
                    style={styles.primaryFullButton}
                    onPress={onSAMLSSOPress}
                    disabled={isLoading}
                  />
                </>
              )}

              <CustomSpacing type="vertical" size="l" />

              <View style={styles.footer}>
                <CustomText text="Forgot your password?" size="s" style={styles.footerText} />
                <CustomButton
                  text="Reset Password"
                  styleType="transparent"
                  size="s"
                  onPress={onResetPasswordPress}
                  accessibilityHint="Press to reset your password"
                />
              </View>
              <CustomSpacing type="vertical" size="s" />

              {allowFormSignup && (
                <View style={styles.footer}>
                  <CustomText text="Not a member?" size="s" style={styles.footerText} />
                  <CustomButton
                    accessibilityHint="Press to sign up"
                    text="Sign Up"
                    styleType="transparent"
                    size="s"
                    onPress={onSignupPress}
                  />
                </View>
              )}
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    width: "100%",
    maxWidth: 450,
    backgroundColor: "#ffffffe6",
    borderRadius: 16,
    padding: 40,
    borderWidth: 1,
    borderColor: "rgba(229,231,235,0.5)",
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 20 },
    elevation: 12,
  },
  cardContent: {
    width: "100%",
    alignItems: "center",
  },
  primaryFullButton: {
    width: "100%",
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 6,
    textAlign: "center",
  },
  logoWrapper: {
    marginBottom: 32,
  },
  title: {
    marginBottom: 8,
    textAlign: "center",
  },
  subtitle: {
    color: "#475467",
    marginBottom: 32,
    textAlign: "center",
  },
  ssoButton: {
    width: "100%",
    marginBottom: 24,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  helpContainer: {
    alignItems: "center",
  },
  helpLink: {
    textDecorationLine: " underline",
    fontWeight: "600",
  },
  helpText: { fontWeight: "600", color: "#475467", textAlign: "center" },
  header: {
    width: "100%",
    alignItems: "center",
  },

  headerTitle: {
    textAlign: "center",
  },
  textInputError: {
    borderColor: "red",
    padding: 9,
  },
  errorText: {
    color: "red",
  },
  errorMessage: {
    color: "red",
    textAlign: "center",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  footerText: {
    color: "#6B7280",
  },
  loader: {
    justifyContent: "center",
    alignItems: "center",
  },
});

export default LoginForm;
